import { eps } from '../services/url.service'
import { ActionType, TargetEntityType, TaskLog } from '../store/task-log/task-log.state'
import { LOG_DETAILS_FIELD_REGISTRY } from './default-generator-field-registry'
import { LogMessageSegment } from './log-message-registry.model'

const formatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: '2-digit',
  year: 'numeric'
})

const DATE_ONLY_REGEX = /^\d{4}-\d{1,2}-\d{1,2}$/
const DATETIME_REGEX = /^\d{4}-\d{1,2}-\d{1,2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/

function formatIfDate(value: string | null): string {
  if (!value) return '-'
  if (!DATE_ONLY_REGEX.test(value) && !DATETIME_REGEX.test(value)) {
    return value
  }
  const parsed = new Date(value)
  if (!isNaN(parsed.getTime())) {
    return formatter.format(parsed)
  }
  return value
}

function parseActionType(action: string): string {
  if (!action) return ''
  return action.replace(/_/g, ' ').toLowerCase()
}

export function getLinkForEntity(
  entityType: TargetEntityType | null,
  entityId: string
): string[] | undefined {
  if (!entityType || !entityId) return undefined

  switch (entityType) {
    case TargetEntityType.EMPLOYEE:
    case TargetEntityType.REPORTING_MANAGER:
    case TargetEntityType.REQUEST_MANAGER:
      return [eps.hrm.employeePath, entityId]
    case 'ROLE':
      return [eps.userRole.rolePath, entityId]
    case 'BUSINESS':
      return [eps.company.businessPath, entityId]
    // Add more cases for other entities
    default:
      return undefined
  }
}

export function generateInitialLogSegments(segments: LogMessageSegment[], log: TaskLog) {
  // --- 1. Actor Segment ---
  segments.push({
    text: log.actorName,
    link: getLinkForEntity(TargetEntityType.EMPLOYEE, log.actorId)
  })

  // --- 2. Action Segment ---
  const action = parseActionType(log.actionType)
  segments.push({ text: ` ${action} ` })

  // --- 3. Affected Entity Segment ---
  if (log.affectedEntityInfo) {
    segments.push({
      text: `${log.affectedEntityInfo}'s`,
      link: getLinkForEntity(log.affectedEntityType, log.affectedEntityId!)
    })
  }
}

/**
 * Automatically builds a log message for any ActionType not in the special-case registry.
 */
export const smartDefaultGenerator = (log: TaskLog): LogMessageSegment[] => {
  const segments: LogMessageSegment[] = []

  generateInitialLogSegments(segments, log)

  // --- 3. Target Entity Type Segment ---
  if (log.targetEntityType) {
    segments.push({
      text: ` ${parseActionType(log.targetEntityType)} `
    })
  }

  // --- 4. Target Segment ---
  if (log.targetEntityInfo) {
    segments.push({
      text: ` ${log.targetEntityInfo}`,
      link: getLinkForEntity(log.targetEntityType, log.targetEntityId!)
    })
  }

  // --- 5. Details Segment ---
  if (log.details?.changes?.field) {
    const { oldValue, newValue } = log.details.changes
    let { field } = log.details.changes
    if (LOG_DETAILS_FIELD_REGISTRY[field]) {
      field = LOG_DETAILS_FIELD_REGISTRY[field]
    }
    if (log.actionType === ActionType.UPDATED) {
      segments.push({
        text: ` ${field} from ${formatIfDate(oldValue)} to ${formatIfDate(newValue)}`
      })
    } else {
      segments.push({
        text: ` ${field} ${formatIfDate(newValue)}`
      })
    }
  }

  return segments
}
